import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getCurrentTenantSettings,
  cancelCurrentTenant,
  revertCancelCurrentTenant,
} from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { formatDate } from '../../utils/formatDateTime';
import { formatPhoneBR } from '../../utils/phone';

const STATUS_BADGE = { active: 'badge-success', suspended: 'badge-danger', trial: 'badge-warning' };
const STATUS_LABEL = { active: 'Ativo', suspended: 'Suspenso', trial: 'Trial' };

function InfoRow({ label, value, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : {}}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: value ? 'var(--gray-900)' : 'var(--gray-300)', fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function formatDateBR(iso) {
  if (!iso) return '—';
  return formatDate(iso);
}

export default function TenantSettings() {
  const confirm = useConfirm();
  const [t, setT] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const reload = () => {
    getCurrentTenantSettings().then(r => setT(r.data.tenant || null)).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  if (!t) return <div className="page-body"><div className="spinner" style={{ margin: '60px auto', display: 'block' }} /></div>;

  const cancelPending = Boolean(t.cancel_requested_at && t.cancel_effective_at && new Date(t.cancel_effective_at) > new Date());

  const handleCancel = async () => {
    const ok = await confirm({
      title: 'Cancelar minha assinatura?',
      message:
        'Você mantém acesso completo por 30 dias (até a data efetiva). Durante esse período pode reverter o cancelamento a qualquer momento. Após 30 dias, a conta é desativada.',
      confirmText: 'Sim, cancelar',
      cancelText: 'Não, voltar',
      variant: 'warning',
      items: [
        'Sem multa, sem fidelidade',
        'Acesso total por 30 dias',
        'Pode reverter a qualquer momento até a data efetiva',
      ],
    });
    if (!ok) return;
    setCancelling(true);
    try {
      const r = await cancelCurrentTenant();
      const when = formatDateBR(r.data.cancel_effective_at);
      toast.success(`Cancelamento agendado para ${when}. Você pode reverter a qualquer momento.`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao processar cancelamento');
    } finally {
      setCancelling(false);
    }
  };

  const handleRevertCancel = async () => {
    const ok = await confirm({
      title: 'Reverter cancelamento?',
      message: 'Sua conta continuará ativa normalmente e o cancelamento agendado será descartado.',
      confirmText: 'Sim, reverter',
      variant: 'success',
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await revertCancelCurrentTenant();
      toast.success('Cancelamento revertido. Sua conta continua ativa.');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao reverter cancelamento');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="page-body">
      <div className="page-header">
        <h2>Configurações</h2>
        <p>Dados cadastrais da sua empresa</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Header da empresa */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 16 }}>
          {t.logo_url ? (
            <img src={t.logo_url} alt="logo"
              style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 10, flexShrink: 0,
                border: '1px solid var(--gray-100)', padding: 6, background: 'var(--surface)' }} />
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: 10, background: t.primary_color || 'var(--primary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 800, color: 'white', fontSize: 22, flexShrink: 0 }}>
              {(t.name || 'A')[0].toUpperCase()}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--gray-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <span className={`badge ${STATUS_BADGE[t.status] || 'badge-gray'}`} style={{ fontSize: 11 }}>
                {STATUS_LABEL[t.status] || t.status}
              </span>
              {t.plan_name && <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{t.plan_name}</span>}
            </div>
          </div>
          {/* Cores */}
          <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
            <div style={{ width: 20, height: 20, borderRadius: 6, background: t.primary_color || '#2563eb', border: '1px solid var(--gray-200)' }} title="Cor primária" />
            <div style={{ width: 20, height: 20, borderRadius: 6, background: t.secondary_color || '#1e40af', border: '1px solid var(--gray-200)' }} title="Cor secundária" />
          </div>
        </div>

        {/* Dados em grid */}
        <div className="card-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
          <InfoRow label="E-mail"   value={t.email} full />
          <InfoRow label="Telefone" value={formatPhoneBR(t.phone)} />
          <InfoRow label="Site"     value={t.website} />
          {!t.plan_name && <InfoRow label="Plano" value={t.plan_name} />}
        </div>
      </div>

      {/* Aviso somente-leitura */}
      <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-100)', borderRadius: 'var(--radius)', padding: '16px 18px', display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
        <span style={{ fontSize: 20, flexShrink: 0, lineHeight: 1.4 }}>🔒</span>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--gray-800)', marginBottom: 4 }}>
            Esses dados são gerenciados pela AtendoApp
          </div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.5 }}>
            Para atualizar o nome, e-mail, plano, cores ou logo da sua empresa, entre em contato com o suporte.
          </div>
        </div>
      </div>

      {/* ═══ Cancelar assinatura ═══ */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          border: cancelPending ? '2px solid #f59e0b' : '1px solid #fecaca',
          borderLeft: `4px solid ${cancelPending ? '#f59e0b' : '#dc2626'}`,
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: cancelPending ? '#9a3412' : '#991b1b' }}>
            {cancelPending ? '⏳ Cancelamento agendado' : 'Cancelar minha assinatura'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.6 }}>
            {cancelPending ? (
              <>
                Você solicitou o cancelamento. Sua conta continua ativa até{' '}
                <strong>{formatDateBR(t.cancel_effective_at)}</strong>. Até lá, você pode usar
                a plataforma normalmente e reverter o cancelamento.
              </>
            ) : (
              <>
                Sem multa, sem fidelidade. Você mantém acesso completo por 30 dias após o clique —
                pode reverter a qualquer momento até a data efetiva.
              </>
            )}
          </div>
        </div>
        <div style={{ padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {cancelPending ? (
            <button
              className={`btn btn-success${cancelling ? ' btn-loading' : ''}`}
              onClick={handleRevertCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Processando...' : '↩️ Reverter cancelamento'}
            </button>
          ) : (
            <button
              className={`btn btn-danger${cancelling ? ' btn-loading' : ''}`}
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Processando...' : '🚪 Cancelar minha assinatura'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
